/**
 * The queue of erasures whose sign-in is still in the tenant (ADR 0012 step 28's follow-up). The portal
 * tells a player their sign-in is removed by a platform administrator within 30 days; these are the
 * sentences an administrator reads to keep that promise. The server decides the figures — days left and
 * whether one is flagged — and this only words them. Imports nothing, so `signInRemovals.test.js` runs it.
 */

export interface SignInRemovalHealth {
  pending: number;
  flagged: number;
  oldestErasedAt: string | null;
}

/**
 * A removal's days, from the server's whole-days-left figure. Zero is "due today", never "0 days left",
 * which reads as time still in hand; below zero is how far past the promise it has gone.
 */
export function daysLeftLine(daysRemaining: number): string {
  if (daysRemaining > 0) return `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left`;
  if (daysRemaining === 0) return 'Due today';
  const overdue = -daysRemaining;
  return `${overdue} ${overdue === 1 ? 'day' : 'days'} overdue`;
}

/**
 * The date an erasure happened, with a named month: it is checked against a request somebody made, and
 * `01/10/2026` is two different days depending on the reader. UTC, the instant's own frame.
 */
export function erasedOn(instant: string): string {
  return new Date(instant).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Platform health's card. Flagged once any erasure is older than 25 days — five days before the promise
 * lapses — and the sentence then leads with how many, since that is the work to do.
 */
export function removalHealthCard(health: SignInRemovalHealth): { value: string; hint: string; flagged: boolean } {
  const value = health.pending.toLocaleString('en-US');
  if (health.pending === 0 || health.oldestErasedAt === null)
    return { value, hint: 'None waiting — every erased account’s sign-in has been removed.', flagged: false };

  const due = 'Each is removed within 30 days of its erasure, in Settings → Sign-ins to remove.';
  if (health.flagged === 0)
    return { value, hint: `Oldest erased ${erasedOn(health.oldestErasedAt)}. ${due}`, flagged: false };

  const lead =
    health.flagged === 1 && health.pending === 1
      ? 'It is older than 25 days'
      : `${health.flagged} of them ${health.flagged === 1 ? 'is' : 'are'} older than 25 days`;
  return { value, hint: `${lead}, so the 30-day promise is close to lapsing. ${due}`, flagged: true };
}
